import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase-config';
import { doc, onSnapshot, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { adminServices, QA_PHASE_POST_APPEAL, isPostAppealQa } from '../services/adminServices';
import { getAiPipelineMode } from '../services/aiPipelineSettings';
import { AI_PIPELINE_LABELS, AI_PIPELINE_LEGACY, isSchemaV2Pipeline } from '../constants/aiPipelineModes';
import { en } from '../locales/en'; 
import { languageManifest as baseLanguageManifest } from '../locales';
import AdminIcon from './AdminIcon';
import { useUser } from '../UserContext';
import { localizationService } from '../services/localizationService';
import { useManagedLanguages } from '../hooks/useManagedLanguages';
import {
  schoolHasUnsettledExternalPayment,
  buildExternalSettlementWarning,
  confirmProceedDespiteUnsettledPayment,
} from '../utils/paymentSettlement';
import { formatVerificationItem } from '../utils/normalizeVerification';
import { computeOverallMaturityScore } from '../utils/overallMaturityScore';
import { openEvidenceFileWithAccessLog, isSchemaV2EvidenceReady } from '../services/aiPipelines/schemaV2';
import { evidenceAnalysisJobRef } from '../services/aiPipelines/schemaV2/schoolSchemaPaths';
import {
  ACCREDITATION_LEVELS,
  accreditationLevelLabel,
  accreditationLevelReportTitle,
  resolveLiveAccreditationLevel,
  resolveRegisteredAccreditationLevel,
} from '../utils/accreditationLevel';
import AccreditationLevelDeltaMark from '../components/AccreditationLevelDeltaMark';

const resolveLangKey = (raw, manifest = baseLanguageManifest) => {
  const key = String(raw || '').trim().toLowerCase();
  if (manifest[key]) return key;
  const match = Object.entries(manifest).find(([, meta]) => {
    return [meta.label, meta.nativeName].some((v) => String(v || '').trim().toLowerCase() === key);
  });
  return match?.[0] || 'en';
};

const ReportEditor = ({ schoolId, onClose, onOpenDossier }) => {
  const { languageManifest } = useManagedLanguages();
  const { user, permissions } = useUser();
  const [school, setSchool] = useState(null);
  const [report, setReport] = useState(null);
  const [audit, setAudit] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pipelineMode, setPipelineMode] = useState(AI_PIPELINE_LEGACY);
  const [evidenceJob, setEvidenceJob] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getAiPipelineMode().then((mode) => {
      if (!cancelled) setPipelineMode(mode);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!schoolId || !isSchemaV2Pipeline(pipelineMode)) {
      setEvidenceJob(null);
      return undefined;
    }
    return onSnapshot(evidenceAnalysisJobRef(schoolId), (snap) => {
      setEvidenceJob(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
  }, [schoolId, pipelineMode]);
  const [localContent, setLocalContent] = useState({}); // Stores unsynced typing
  const [distilledData, setDistilledData] = useState(null); // The "Clean Silo"
  const [isPreview, setIsPreview] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [rejectionFeedback, setRejectionFeedback] = useState("");
  const [badgeVariant, setBadgeVariant] = useState('medallion'); // Added for badge switcher
  const [editorNotice, setEditorNotice] = useState(null); // { type: 'success'|'error'|'info', text: string }
  const noticeTimerRef = useRef(null);
  const [contactNoteDraft, setContactNoteDraft] = useState('');
  const [appendingContact, setAppendingContact] = useState(false);
  /** `undefined` = snapshot not yet received; `null` = no staff_workspace doc; object = staff-only silo data */
  const [staffWorkspace, setStaffWorkspace] = useState(undefined);
  const [factCheckLens, setFactCheckLens] = useState({ executiveSummary: '', stationContent: {} });
  const [factCheckLensView, setFactCheckLensView] = useState('en');
  const evidenceDraftMinStep = isSchemaV2Pipeline(pipelineMode) ? 2 : 3;
  const draftEvidenceReady = isSchemaV2Pipeline(pipelineMode)
    ? isSchemaV2EvidenceReady(evidenceJob) ||
      (report?.meta?.refinery?.evidence?.step || 0) >= evidenceDraftMinStep
    : (report?.meta?.refinery?.evidence?.step || 0) >= evidenceDraftMinStep;

  const showNotice = useCallback((text, type = 'info', autoMs = 6500) => {
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    setEditorNotice({ type, text });
    if (autoMs > 0) {
      noticeTimerRef.current = setTimeout(() => {
        setEditorNotice(null);
        noticeTimerRef.current = null;
      }, autoMs);
    }
  }, []);

  useEffect(
    () => () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    },
    []
  );

  const assignedAccreditorUid = school?.assignedAuditorUid || null;
  const reportAccreditorUid = report?.certification?.accreditorUid || null;
  const reportAccreditorMatchesAssignment =
    Boolean(assignedAccreditorUid) && assignedAccreditorUid === reportAccreditorUid;
  const leadAccreditorName =
    school?.assignedAuditorName ||
    report?.certification?.accreditorName ||
    "TBD (No Auditor Assigned)";
  const leadAccreditorQuals =
    !assignedAccreditorUid || reportAccreditorMatchesAssignment
      ? (report?.certification?.accreditorQuals || '')
      : '';
  const leadAccreditorBio =
    !assignedAccreditorUid || reportAccreditorMatchesAssignment
      ? (report?.certification?.accreditorBio || '')
      : '';

  useEffect(() => {
    const schoolRef = doc(db, "schools", schoolId);
    const reportRef = doc(db, "reports", schoolId);
    const auditRef = doc(db, "audits", schoolId);
    const distilledRef = doc(db, "distilledData", schoolId);

    const unsubDistilled = onSnapshot(distilledRef, (snapshot) => {
      if (snapshot.exists()) setDistilledData(snapshot.data());
    });
    const unsubSchool = onSnapshot(schoolRef, (snapshot) => {
      if (snapshot.exists()) setSchool(snapshot.data());
    });

    const unsubReport = onSnapshot(reportRef, (snapshot) => {
      if (snapshot.exists()) {
        setReport(snapshot.data());
      } else {
        adminServices.initializeReport(schoolId);
      }
    });

    const unsubAudit = onSnapshot(auditRef, (snapshot) => {
      if (snapshot.exists()) setAudit(snapshot.data());
    });

    // CLEANUP: All listeners including unsubDistilled
    return () => { 
      unsubSchool(); 
      unsubReport(); 
      unsubAudit(); 
      unsubDistilled(); 
    };
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    adminServices.migrateReportStaffWorkspaceFromLegacyIfNeeded(schoolId).catch((e) =>
      console.error('migrateReportStaffWorkspaceFromLegacyIfNeeded:', e)
    );
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    const wsRef = doc(db, 'reports', schoolId, 'staff_workspace', 'data');
    const unsub = onSnapshot(wsRef, (snap) => {
      setStaffWorkspace(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [schoolId]);

  useEffect(() => {
    const schoolLang = resolveLangKey(school?.currentLanguage || school?.signupLanguage || 'en', languageManifest);
    if (!schoolId || !report || schoolLang === 'en') {
      setFactCheckLens({ executiveSummary: '', stationContent: {} });
      setFactCheckLensView('en');
      return;
    }
    let cancelled = false;

    const loadLens = async () => {
      try {
        const summaryDocId = `report_${schoolId}_summary`;
        const summarySource = { exec: report?.institutionalBrief?.executiveSummary || '' };
        const translatedSummary = await localizationService.sync(
          schoolLang,
          summaryDocId,
          summarySource,
          'v2_report_masters',
          'v2_report_translations'
        );

        const stationContent = {};
        const stationIds = Object.keys(report?.stationAnalysis || {});
        for (const sId of stationIds) {
          const source = { content: report?.stationAnalysis?.[sId]?.content || '' };
          const contentDocId = `report_${schoolId}_s${sId}`;
          const translated = await localizationService.sync(
            schoolLang,
            contentDocId,
            source,
            'v2_report_masters',
            'v2_report_translations'
          );
          stationContent[sId] = translated?.content || source.content;
        }

        if (!cancelled) {
          setFactCheckLens({
            executiveSummary: translatedSummary?.exec || '',
            stationContent
          });
        }
      } catch (err) {
        console.error('ReportEditor fact-check lens sync error:', err);
      }
    };

    loadLens();
    return () => { cancelled = true; };
  }, [schoolId, school?.currentLanguage, school?.signupLanguage, report, languageManifest]);

  const handleUpdate = async (path, value) => {
    setIsSaving(true);
    try {
      const reportRef = doc(db, "reports", schoolId);
      const patch = {
        [path]: value,
        "meta.lastEditedAt": serverTimestamp()
      };
      // Station maturity tweaks refresh Institutional Maturity Score + benchmark UI
      const ratingMatch = /^stationAnalysis\.(\d{2})\.ifipRating$/.exec(path);
      if (ratingMatch) {
        const sId = ratingMatch[1];
        const nextStations = {
          ...(report?.stationAnalysis || {}),
          [sId]: {
            ...(report?.stationAnalysis?.[sId] || {}),
            ifipRating: value,
          },
        };
        patch["meta.overallMaturityScore"] = computeOverallMaturityScore(nextStations);
      }
      await updateDoc(reportRef, patch);
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  /** Local draft while typing; Firestore sync on blur avoids cursor jump from onSnapshot. */
  const getEditorFieldValue = (path, remoteValue) =>
    localContent[path] !== undefined ? localContent[path] : (remoteValue ?? '');

  const queueEditorField = (path, value) => {
    setLocalContent((prev) => ({ ...prev, [path]: value }));
  };

  const commitEditorField = (path, value) => {
    handleUpdate(path, value);
    setLocalContent((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const handleInternalNotesUpdate = async (value) => {
    setIsSaving(true);
    try {
      const wsRef = doc(db, 'reports', schoolId, 'staff_workspace', 'data');
      await setDoc(wsRef, { internalNotes: value, updatedAt: serverTimestamp() }, { merge: true });
      const reportRef = doc(db, 'reports', schoolId);
      await updateDoc(reportRef, { 'meta.lastEditedAt': serverTimestamp() });
    } catch (err) {
      console.error('Save internal notes error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const formatContactLogTime = (createdAt) => {
    try {
      const d = createdAt?.toDate ? createdAt.toDate() : createdAt;
      if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return '';
    }
  };

  const handleAppendContactLog = async () => {
    const text = contactNoteDraft.trim();
    if (!text || isLocked || !permissions.canDraftReport) return;
    setAppendingContact(true);
    try {
      await adminServices.appendAccreditorContactLogEntry({
        schoolId,
        schoolName: school?.officialName,
        text,
        actorName: user?.displayName || user?.email || 'Staff',
        actorUid: user?.uid,
      });
      setContactNoteDraft('');
      showNotice('Contact log entry saved.', 'success', 4000);
    } catch (e) {
      showNotice(e?.message || String(e), 'error', 0);
    } finally {
      setAppendingContact(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!draftEvidenceReady) {
      showNotice(
        isSchemaV2Pipeline(pipelineMode)
          ? "Evidence analysis is not finished yet. Wait for the job board to complete before generating the draft."
          : "The Data Refinery is still distilling evidence. Please wait for the 'Stored' status before generating the full report.",
        'info',
        8000
      );
      return;
    }

    if (!window.confirm("This will overwrite existing draft content with a new AI-synthesized report. Proceed?")) return;

    if (school && schoolHasUnsettledExternalPayment(school)) {
      const ok = confirmProceedDespiteUnsettledPayment(
        buildExternalSettlementWarning(school.officialName, 'generate AI report draft')
      );
      if (!ok) return;
    }

    setIsGenerating(true);
    try {
      // Master report generation is always English.
      // School-language translation is a separate post-release lens warm-up.
      await adminServices.generateReportDraft(schoolId, user, 'English');
      showNotice('AI Draft Report Complete.', 'success');
    } catch (err) {
      console.error("Generation error:", err);
      showNotice('Failed to generate draft: ' + err.message, 'error', 0);
    } finally {
      setIsGenerating(false);
    }
  };

  const getLevelLabel = (stationId, level) => {
    if (!level) return "Not Assessed";
    const stationData = en.stations.find(s => s.id === stationId);
    return stationData?.diagnosticGrid[level]?.label || `Level ${level}`;
  };

  const getComparisonIcon = (self, ifip) => {
    if (!self || !ifip) return <span style={{ color: '#ccc' }}>?</span>;
    const s = parseInt(self);
    const i = parseInt(ifip);
    if (i > s) return <span style={{ color: '#4ade80', fontWeight: 'bold' }}>↑</span>;
    if (i < s) return <span style={{ color: '#f87171', fontWeight: 'bold' }}>↓</span>;
    return <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>=</span>;
  };

  const toggleSectionApproval = async (sectionKey) => {
    if (isLocked) return; // Security Guard: Prevent any state changes to a finalised record // Added
    const currentStatus = report.approvals?.[sectionKey] || false;
    await handleUpdate(`approvals.${sectionKey}`, !currentStatus);
  };

  const handleAccreditationLevelChange = async (event) => {
    const next = event.target.value;
    const current = resolveLiveAccreditationLevel(school);
    if (!next || next === current) return;

    const fromLabel = accreditationLevelLabel(current, { long: true });
    const toLabel = accreditationLevelLabel(next, { long: true });
    const registeredLabel = accreditationLevelLabel(resolveRegisteredAccreditationLevel(school), { long: true });
    const step = String(school.workflowStep || '').toLowerCase();
    const hasDraftContent = Boolean(
      String(report?.institutionalBrief?.executiveSummary || '').trim() ||
      Object.keys(report?.stationAnalysis || {}).length
    );
    const schoolHasSeenReport = ['fact_check', 'appeals', 'accepted'].includes(step);

    let message =
      `Change this cycle from ${fromLabel} to ${toLabel}?\n\n` +
      `The school portal, registry, and dossier will update immediately. ` +
      `The issued certificate and report history will use ${toLabel} when you finalise.\n\n` +
      `Registered / requested level: ${registeredLabel}.`;
    if (hasDraftContent) {
      message += `\n\nThe current draft was written for ${fromLabel}. Consider regenerating the AI draft.`;
    }
    if (schoolHasSeenReport) {
      message += `\n\nThe school has already seen this report. They will see the new tier on their next view.`;
    }
    if (!window.confirm(message)) return;

    setIsSaving(true);
    try {
      const result = await adminServices.setSchoolAccreditationLevel(schoolId, next, user);
      if (result?.changed) {
        showNotice(`Awarded tier set to ${toLabel}.`, 'success');
      }
    } catch (err) {
      console.error('Accreditation level update failed:', err);
      showNotice('Could not change accreditation level: ' + (err.message || err), 'error', 0);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishReport = async () => {
    if (!window.confirm("FINAL PUBLICATION: This will lock the report, issue the certificate, and archive this cycle. Proceed?")) return;
    
    setIsSaving(true);
    try {
      // Use the centralized finalize method which handles archiving
      await adminServices.finalizeAccreditation(schoolId, user);
      // Benchmarking snapshot + live report reset occur inside finalizeAccreditation

      // Log the event
      await adminServices.logAction(user.displayName, "PUBLISH_FINAL_REPORT", { schoolId, schoolName: school.officialName });

      showNotice('Accreditation cycle finalised and published.', 'success');
    } catch (err) {
      console.error("Publishing error:", err);
      showNotice('Failed to publish: ' + err.message, 'error', 0);
    } finally {
      setIsSaving(false);
    }
  };

  const handleWorkflowAction = async () => {
    const step = school.workflowStep || 'submitted';
    setIsSaving(true);
    try {
      if (['submitted', 'report_compiling', 'internal_review_rejected'].includes(step)) {
        await adminServices.submitForPeerReview(schoolId, school.officialName, user.displayName);
        showNotice('Report submitted for internal peer review.', 'success');
      } else if (step === 'peer_review') {
        // Updated: Pull the report back to drafting
        await adminServices.retractReport(schoolId, 'report_compiling', school.officialName, user.displayName);
        showNotice('Report retracted to drafting.', 'success');
      } else if (step === 'accepted') {
        await handlePublishReport();
      }
    } catch (err) {
      showNotice('Workflow transition failed: ' + err.message, 'error', 0);
    } finally {
      setIsSaving(false);
    }
  };
  const handleRejectReport = async () => {
    if (!rejectionFeedback.trim()) {
      showNotice('Please provide specific feedback for the author before rejecting.', 'info', 6000);
      return;
    }
    setIsSaving(true);
    try {
      await adminServices.submitReviewFeedback(schoolId, rejectionFeedback, school.officialName, user.displayName);
      setShowRejectionForm(false);
      setRejectionFeedback('');
      showNotice('Report sent back to author for revisions.', 'success');
    } catch (err) {
      showNotice('Failed to submit rejection: ' + err.message, 'error', 0);
    } finally {
      setIsSaving(false);
    }
  };

      // LOADING GUARD: Must remain above logic that reads report/audit/school
  if (!school || !report || !audit) return <div className="admin-placeholder">Loading Editor...</div>;

  const schoolLang = resolveLangKey(school?.currentLanguage || school?.signupLanguage || 'en', languageManifest);
  const hasLocalizedLens = schoolLang !== 'en';
  const factCheckStructured = report?.factCheck;
  const legacyFeedback = report?.factCheckFeedback || {};
  const hasStructuredFactCheck = Boolean(factCheckStructured && typeof factCheckStructured === 'object');
  const feedbackLanguage = hasStructuredFactCheck ? (factCheckStructured.language || schoolLang || 'en') : (schoolLang || 'en');

  const getFeedbackNode = (sectionKey) => {
    if (hasStructuredFactCheck) {
      if (sectionKey === 'general') return factCheckStructured?.general || null;
      return factCheckStructured?.sections?.[sectionKey] || null;
    }
    const fallbackText = sectionKey === 'general' ? legacyFeedback?.general : legacyFeedback?.[sectionKey];
    if (!fallbackText) return null;
    return {
      originalText: fallbackText,
      originalLang: feedbackLanguage,
      translatedEn: fallbackText,
      translationStatus: 'legacy'
    };
  };

  const getFactCheckReportText = (sectionKey) => {
    if (factCheckLensView === 'school' && hasLocalizedLens) {
      if (sectionKey === 'general') {
        return factCheckLens.executiveSummary || report.institutionalBrief?.executiveSummary || '';
      }
      const stationId = sectionKey.slice(1);
      return factCheckLens.stationContent?.[stationId] || report.stationAnalysis?.[stationId]?.content || '';
    }
    if (sectionKey === 'general') return report.institutionalBrief?.executiveSummary || '';
    const stationId = sectionKey.slice(1);
    return report.stationAnalysis?.[stationId]?.content || '';
  };

  const getFactCheckEditablePath = (sectionKey) => {
    if (sectionKey === 'general') return 'institutionalBrief.executiveSummary';
    const stationId = sectionKey.slice(1);
    return `stationAnalysis.${stationId}.content`;
  };

  const getFactCheckEditableValue = (sectionKey) => {
    const path = getFactCheckEditablePath(sectionKey);
    if (sectionKey === 'general') {
      return getEditorFieldValue(path, report.institutionalBrief?.executiveSummary || '');
    }
    const stationId = sectionKey.slice(1);
    return getEditorFieldValue(path, report.stationAnalysis?.[stationId]?.content || '');
  };

  // LOGIC CALCULATIONS: Safe to run now that report is guaranteed not null
  const contactLogEntries =
    staffWorkspace != null
      ? (staffWorkspace.accreditorContactLog || [])
      : (report.meta?.accreditorContactLog || []);
  const internalNotesValue =
    staffWorkspace != null ? (staffWorkspace.internalNotes ?? '') : (report.internalNotes || '');

    const requiredKeys = ['brief', 'matrix', 'station_01', 'station_02', 'station_03', 'station_04', 'station_05', 'station_06', 'station_07', 'evidence', 'roadmap', 'signoff'];
    const postAppealQa = isPostAppealQa(report.meta);
    const isLocked = report.meta?.isPublished === true; // Moved here for safety // Added
    const levelLocked =
      isLocked ||
      String(school.workflowStep || '').toLowerCase() === 'finalised' ||
      !permissions.canDraftReport;
    const awardedLevel = resolveLiveAccreditationLevel(school);
    const approvedCount = requiredKeys.filter(key => report.approvals?.[key] === true).length;
    const isReadyToPublish = approvedCount === requiredKeys.length;
    const reportYear = report.meta?.publishedAt ? report.meta.publishedAt.toDate().getFullYear() : new Date().getFullYear();
    const reportIdSuffix = schoolId.slice(0, 4).toUpperCase();
    const reportRefId = `IFIP-${reportYear}-${reportIdSuffix}`;
    // Define precise validity window (24 Months)
    const publishedDateObj = report.meta?.publishedAt ? report.meta.publishedAt.toDate() : new Date();
    const expiryDateObj = new Date(publishedDateObj);
    expiryDateObj.setFullYear(expiryDateObj.getFullYear() + 2); // Explicit 24-month validity logic // Added

  const displayExpiryDate = expiryDateObj.toLocaleDateString('en-GB', { 
    day: '2-digit', month: 'long', year: 'numeric' 
  }); // Added
  
  const displayDate = report.meta?.publishedAt 
    ? report.meta.publishedAt.toDate().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  const SectionHeader = ({ title }) => (
    <div className="section-header-container">
      <h2 className="section-header-title">{title.toUpperCase()}</h2>
    </div>
  );

  return (
    <div className={`report-editor-v2 ${isPreview ? 'preview-mode' : ''}`}>
      {editorNotice && (
        <div
          role="alert"
          className={`report-editor-notice report-editor-notice--${editorNotice.type}`}
        >
          <span className="report-editor-notice__text">{editorNotice.text}</span>
          <button
            type="button"
            className="report-editor-notice__dismiss"
            onClick={() => {
              if (noticeTimerRef.current) {
                clearTimeout(noticeTimerRef.current);
                noticeTimerRef.current = null;
              }
              setEditorNotice(null);
            }}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      )}

      {/* HEADER */}
      <header className="editor-sticky-header">
        <div className="editor-header-left">
          <button onClick={onClose} className="btn-back">← BACK TO REGISTRY</button>
          <strong 
            onClick={() => onOpenDossier(schoolId)} 
            style={{ cursor: 'pointer', color: '#138a8a', textDecoration: 'underline', marginLeft: '10px' }}
          >
            {school.officialName}
          </strong>
          <label className="editor-level-control">
            <span className="editor-level-control__label">Awarded tier</span>
            <span className="editor-level-control__row">
              <select
                className="editor-level-control__select"
                value={awardedLevel}
                onChange={handleAccreditationLevelChange}
                disabled={levelLocked || isSaving}
                aria-label="Awarded accreditation tier"
                title={
                  levelLocked
                    ? (String(school.workflowStep || '').toLowerCase() === 'finalised' || isLocked
                      ? 'Locked after finalise'
                      : 'Edit & draft reports permission required')
                    : 'Change the target / awarded accreditation tier'
                }
              >
                {ACCREDITATION_LEVELS.map((key) => (
                  <option key={key} value={key}>
                    {accreditationLevelLabel(key, { long: true })}
                  </option>
                ))}
              </select>
              <AccreditationLevelDeltaMark school={school} />
            </span>
          </label>
        </div>
        
        <div className="editor-header-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
          {pipelineMode && pipelineMode !== AI_PIPELINE_LEGACY ? (
            <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginBottom: 4 }}>
              AI pipeline: {AI_PIPELINE_LABELS[pipelineMode] || pipelineMode}
              {report.meta?.aiPipeline ? ' · draft tagged ' + report.meta.aiPipeline : ''}
            </div>
          ) : null}
          {/* ROW 1: PRIMARY ACTIONS */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button 
              className="btn-secondary"
              onClick={handleGenerateReport}
              disabled={isGenerating || !draftEvidenceReady || !permissions.canRunAI || isLocked}
              style={{ 
                background: isGenerating ? '#fbbf24' : 'rgba(19, 138, 138, 0.1)', 
                borderColor: '#138a8a', color: '#138a8a',
                opacity: (!draftEvidenceReady || !permissions.canRunAI || isLocked) ? 0.5 : 1
              }}
              title={!permissions.canRunAI ? "Insufficient permissions to trigger AI Synthesis" : ""}
            >
              {isGenerating ? "Synthesizing Report..." : 
               !draftEvidenceReady ? "Waiting for analysis..." : "Generate AI Draft"}
            </button>

            <button 
              className="btn-secondary"
              onClick={() => setIsPreview(!isPreview)}
              style={{ borderColor: isPreview ? '#d11d7e' : 'rgba(255,255,255,0.2)', color: isPreview ? '#d11d7e' : 'white' }}
            >
              {isPreview ? "Edit Report" : "School's View"}
            </button>
            
            {!isPreview && <span className="sync-status" style={{ fontSize: '0.7rem', opacity: 0.5 }}>{isSaving ? 'Syncing...' : 'Saved'}</span>}
            
            {(() => {
              const step = school.workflowStep || 'submitted';
              let label = "Submit for Peer Review";
              let isDisabled = !isReadyToPublish;
              let title = !isReadyToPublish ? `Awaiting Approvals (${approvedCount}/12)` : "";

              if (step === 'peer_review') {
                label = "Submitted for Internal Review"; // Updated label to reflect state
                isDisabled = true; // Disable primary button; secondary actions handled below or in registry
                title = "This report is currently being reviewed by the IFIP Quality Team.";
              } else if (step === 'fact_check') {
                label = "Released to School for Fact-Check"; // Updated label to reflect state
                isDisabled = true;
              } else if (step === 'appeals') {
                label = "Amend draft — use Send to QA below";
                isDisabled = true;
              } else if (['submitted', 'report_compiling', 'internal_review_rejected'].includes(step)) {
                label = "Submit for Internal Review";
                isDisabled = !isReadyToPublish;
              } else if (step === 'accepted') {
                label = "Finalise & Issue Certificate";
                isDisabled = !(permissions.canPublish || permissions.canPeerReview);
                title = !(permissions.canPublish || permissions.canPeerReview) ? "Publish or peer-review permission required" : "";
              } else if (step === 'finalised') {
                label = "Cycle Finalised";
                isDisabled = true;
              }

              return (
                <button 
                  className="btn-activate" 
                  onClick={handleWorkflowAction}
                  disabled={isDisabled}
                  style={{ opacity: isDisabled ? 0.5 : 1 }}
                  title={title}
                >
                  {label}
                </button>
              );
            })()}
          </div>

          {/* ROW 2: APPROVAL DASHBOARD */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '0.65rem', opacity: 0.5, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', color: 'white' }}>
              Sections Marked as Final:
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              {requiredKeys.map(key => {
                const isDone = report.approvals?.[key];
                const label = key.startsWith('station_') ? key.split('_')[1].slice(1) : key.charAt(0).toUpperCase();
                return (
                  <button
                    key={key}
                    onClick={() => toggleSectionApproval(key)}
                    style={{
                      width: '22px', height: '22px', borderRadius: '3px', 
                      border: `1px solid ${isDone ? '#138a8a' : '#444'}`,
                      background: isDone ? '#138a8a' : 'transparent', 
                      color: isDone ? 'white' : '#666',
                      fontSize: '0.55rem', fontWeight: 'bold', cursor: 'pointer'
                    }}
                    title={`Toggle ${key.toUpperCase()}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* Accreditor call / exchange log — append-only; audited via systemLogs; fed into AI draft synthesis */}
      {!isPreview && (
        <details
          className="internal-notes-box"
          style={{
            borderColor: 'rgba(19, 138, 138, 0.45)',
            background: 'rgba(19, 138, 138, 0.06)',
            marginBottom: '16px',
          }}
        >
          <summary
            style={{
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              color: '#5eead4',
              letterSpacing: '0.04em',
            }}
          >
            ACCREDITOR CONTACT & EXCHANGE LOG (append-only, audited)
          </summary>
          <p style={{ margin: '10px 0 12px', fontSize: '0.72rem', opacity: 0.75, lineHeight: 1.45 }}>
            Record substantive calls, meetings, or email exchanges with the school. Entries are stored in a staff-only
            workspace (not on the school-readable report document), logged in System Logs, and included when you run{' '}
            <strong>Generate AI Draft</strong>—treat as confidential clinical background, not text for the school-facing
            report verbatim.
          </p>
          {contactLogEntries.length > 0 && (
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 14px',
                maxHeight: '220px',
                overflowY: 'auto',
                fontSize: '0.78rem',
                lineHeight: 1.45,
              }}
            >
              {[...contactLogEntries]
                .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                .map((entry, idx) => (
                  <li
                    key={`${entry.createdAt?.seconds || idx}-${entry.authorUid || ''}-${idx}`}
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                      padding: '10px 0',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <div style={{ opacity: 0.65, fontSize: '0.65rem', marginBottom: '4px' }}>
                      {formatContactLogTime(entry.createdAt)}
                      {entry.authorName ? ` · ${entry.authorName}` : ''}
                    </div>
                    {entry.text}
                  </li>
                ))}
            </ul>
          )}
          <textarea
            className="editor-textarea"
            style={{ minHeight: '72px', background: 'rgba(0,0,0,0.2)', borderColor: 'rgba(19, 138, 138, 0.35)' }}
            value={contactNoteDraft}
            onChange={(e) => setContactNoteDraft(e.target.value)}
            placeholder="New entry (saved as append only when you click Add to log)…"
            disabled={isLocked || !permissions.canDraftReport}
          />
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleAppendContactLog}
              disabled={
                isLocked ||
                !permissions.canDraftReport ||
                appendingContact ||
                !contactNoteDraft.trim()
              }
              style={{ borderColor: '#138a8a', color: '#5eead4' }}
            >
              {appendingContact ? 'Saving…' : 'Add to log'}
            </button>
          </div>
        </details>
      )}
      
      {/* 00. OFFICIAL DOCUMENT HEADER (Visible only in Preview/School View) */}
      {isPreview && (
        <div className="official-document-header" style={{ 
          background: 'white', 
          color: '#1a2a44', 
          padding: '50px 60px', 
          borderBottom: '5px solid #138a8a', 
          marginBottom: '50px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <img src="/images/01%20IFIP%20Logo.png" alt="IFIP" style={{ height: '70px', marginBottom: '30px' }} />
              <h1 style={{ margin: 0, fontSize: '3rem', fontWeight: '800', color: '#1a2a44', letterSpacing: '-1px' }}>
                {school.officialName}
              </h1>
              <div style={{ 
                display: 'inline-block', 
                background: '#138a8a', 
                color: 'white', 
                padding: '6px 16px', 
                borderRadius: '4px', 
                fontSize: '0.9rem', 
                fontWeight: 'bold', 
                marginTop: '15px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                {accreditationLevelReportTitle(school.accreditationLevel)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#138a8a', letterSpacing: '1px' }}>{reportRefId}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>{displayDate}</div>
              <div style={{ marginTop: '30px' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, letterSpacing: '1px', fontWeight: 'bold' }}>Lead Auditor</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#1a2a44' }}>{leadAccreditorName}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>{leadAccreditorQuals}</div>
              </div>
            </div>
          </div>
          <div style={{ 
            marginTop: '40px', 
            paddingTop: '20px', 
            borderTop: '1px solid rgba(0,0,0,0.1)', 
            fontSize: '0.8rem', 
            fontStyle: 'italic', 
            opacity: 0.5,
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span>Report Status: {report.meta?.isPublished ? "Final" : "Draft"}</span>
            <span>Confidential to IFIP and {school.officialName}</span>
          </div>
        </div>
      )}

      {/* EXPIRY BANNER: Visible in Preview/Published mode to clarify 24-month window */}
      {isPreview && report.meta?.isPublished && (
        <div style={{ 
          margin: '-20px 60px 40px 60px', 
          padding: '12px 20px', 
          background: '#f8fafc', 
          borderLeft: '4px solid #138a8a', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontSize: '0.85rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}>
          <span style={{ color: '#64748b', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Accreditation Validity Period
          </span>
          <span style={{ color: '#1e293b', fontWeight: 'bold' }}>
            {displayDate} — {displayExpiryDate}
          </span>
        </div>
      )}

      {/* INTERNAL NOTES (SHADOW LAYER) */}
      {!isPreview && (
        <div className="internal-notes-box">
            <h4 style={{ margin: '0 0 10px 0', fontSize: '0.75rem', color: '#d11d7e' }}>INTERNAL NOTES (ACCREDITOR ONLY)</h4>
            <textarea 
              className="editor-textarea"
              style={{ minHeight: '80px', background: 'transparent', borderColor: '#d11d7e33' }}
              value={internalNotesValue}
              onChange={(e) => handleInternalNotesUpdate(e.target.value)}
              placeholder="Private flags, concerns, or highlights..."
            />
        </div>
      )}
      {/* INTERNAL PEER REVIEW FEEDBACK (REVISIONS MODE) */}
      {school.workflowStep === 'internal_review_rejected' && report.internalReviewFeedback && (
        <div className="internal-notes-box" style={{ borderColor: '#d11d7e', background: 'rgba(209, 29, 126, 0.05)', marginBottom: '30px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.75rem', color: '#d11d7e' }}>PEER REVIEW REVISIONS REQUESTED</h4>
          <div style={{ fontSize: '0.85rem', color: '#fff', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
            <p><strong>Reviewer ({report.reviewerName || 'IFIP Quality Team'}):</strong> {report.internalReviewFeedback}</p>
          </div>
        </div>
      )}
      {/* FACT-CHECK FEEDBACK (APPEALS MODE) */}
      {school.workflowStep === 'appeals' && (
        <div className="internal-notes-box" style={{ borderColor: '#138a8a', background: 'rgba(19, 138, 138, 0.08)', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.75rem', color: '#138a8a' }}>POST-APPEAL — ACCREDITOR</h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.5 }}>
            Incorporate amendments below, then send to QA. The school will not see the draft again until QA releases it or approves finalisation.
          </p>
          <button
            type="button"
            className="btn-activate"
            disabled={!(permissions.canDraftReport || school.assignedAuditorUid === user.uid)}
            style={{ opacity: (permissions.canDraftReport || school.assignedAuditorUid === user.uid) ? 1 : 0.5 }}
            onClick={async () => {
              setIsSaving(true);
              try {
                await adminServices.submitAppealForQa(schoolId, school.officialName, user.displayName);
                showNotice('Amended draft sent to QA.', 'success');
              } catch (err) {
                showNotice('Failed to send to QA: ' + err.message, 'error', 0);
              } finally {
                setIsSaving(false);
              }
            }}
          >
            Send Amended Draft to QA
          </button>
        </div>
      )}

      {school.workflowStep === 'appeals' && report.factCheckFeedback && (
        <div className="internal-notes-box" style={{ borderColor: '#fbbf24', background: 'rgba(251, 191, 36, 0.05)', marginBottom: '30px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.75rem', color: '#fbbf24' }}>SCHOOL ACCURACY FEEDBACK (REVISIONS REQUESTED)</h4>
          <div style={{ fontSize: '0.85rem', color: '#fff', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
            {report.factCheckFeedback.general && (
              <p><strong>General:</strong> {report.factCheckFeedback.general}</p>
            )}
            {Object.keys(report.factCheckFeedback).filter(k => k.startsWith('s0')).map(key => (
              <p key={key}><strong>Station {key.slice(1)}:</strong> {report.factCheckFeedback[key]}</p>
            ))}
          </div>
        </div>
      )}

      {/* FACT-CHECK MIRROR REVIEW: Accreditor sees school-style report + comments with EN/local toggle */}
      {(school.workflowStep === 'fact_check' || school.workflowStep === 'appeals' || school.workflowStep === 'accepted') && (
        <section className="editor-card" style={{ borderColor: '#138a8a', marginBottom: '30px' }}>
          <SectionHeader title="Fact-Check Mirror Review" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '10px', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.8rem' }}>
              Mirror of the school fact-check view. Content toggle is read-only; all edits remain in master English sections below.
            </p>
            <div style={{ display: 'inline-flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setFactCheckLensView('en')}
                className="btn-secondary"
                style={{
                  padding: '6px 12px',
                  borderColor: '#138a8a',
                  color: '#138a8a',
                  background: factCheckLensView === 'en' ? 'rgba(19,138,138,0.15)' : 'transparent'
                }}
              >
                Master EN
              </button>
              <button
                type="button"
                disabled={!hasLocalizedLens}
                onClick={() => setFactCheckLensView('school')}
                className="btn-secondary"
                style={{
                  padding: '6px 12px',
                  borderColor: '#138a8a',
                  color: '#138a8a',
                  opacity: hasLocalizedLens ? 1 : 0.5,
                  background: factCheckLensView === 'school' ? 'rgba(19,138,138,0.15)' : 'transparent'
                }}
                title={hasLocalizedLens ? `Toggle to ${schoolLang.toUpperCase()} school lens` : 'School language is English'}
              >
                School Lens ({schoolLang.toUpperCase()})
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '12px', fontSize: '0.75rem', color: '#94a3b8' }}>
            Comment source language: <strong style={{ color: '#fff' }}>{feedbackLanguage.toUpperCase()}</strong>
            {hasStructuredFactCheck ? '' : ' (legacy feedback format)'}
          </div>

          <div style={{ display: 'grid', gap: '14px' }}>
            {['general', 's01', 's02', 's03', 's04', 's05', 's06', 's07'].map((sectionKey) => {
              const node = getFeedbackNode(sectionKey);
              const sectionTitle = sectionKey === 'general'
                ? `Public portrait / ${school?.officialName || 'School'}`
                : `Station ${sectionKey.slice(1)}`;
              return (
                <div key={sectionKey} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', fontWeight: 'bold', color: '#dbeafe', fontSize: '0.8rem' }}>
                    {sectionTitle}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
                    <div style={{ padding: '14px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '8px' }}>
                        Report text ({factCheckLensView === 'school' && hasLocalizedLens ? schoolLang.toUpperCase() : 'EN'})
                      </div>
                      {factCheckLensView === 'en' ? (
                        <textarea
                          className="editor-textarea"
                          style={{
                            minHeight: sectionKey === 'general' ? '140px' : '180px',
                            background: 'rgba(19, 138, 138, 0.05)',
                            borderColor: 'rgba(19, 138, 138, 0.4)',
                            color: '#e2e8f0'
                          }}
                          value={getFactCheckEditableValue(sectionKey)}
                          onChange={(e) => {
                            queueEditorField(getFactCheckEditablePath(sectionKey), e.target.value);
                          }}
                          onBlur={(e) => commitEditorField(getFactCheckEditablePath(sectionKey), e.target.value)}
                          placeholder="Edit master English report text here while reviewing school comments..."
                          disabled={isLocked || !permissions.canDraftReport}
                        />
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', fontSize: '0.86rem', color: '#e2e8f0' }}>
                          {getFactCheckReportText(sectionKey) || '—'}
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '14px' }}>
                      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#fbbf24', marginBottom: '8px' }}>
                        School comment
                      </div>
                      {node ? (
                        <>
                          <div style={{ fontSize: '0.78rem', color: '#f8fafc', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
                            <strong style={{ color: '#facc15' }}>Original:</strong> {node.originalText || '—'}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#cbd5e1', whiteSpace: 'pre-wrap' }}>
                            <strong style={{ color: '#93c5fd' }}>English rendering:</strong> {node.translatedEn || '—'}
                          </div>
                        </>
                      ) : (
                        <div style={{ color: '#64748b', fontSize: '0.8rem' }}>No comment submitted for this section.</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 01. INSTITUTIONAL BRIEF */}
      <section className="editor-card">
        <SectionHeader title="01. Institutional Brief" sectionKey="brief" />
        
        {/* NEW: Maturity Score Indicator */}
        {report.meta?.overallMaturityScore && (
          <div style={{ 
            background: 'rgba(19, 138, 138, 0.1)', 
            border: '1px solid #138a8a', 
            borderRadius: '8px', 
            padding: '15px', 
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
          }}>
            <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#138a8a' }}>
              {report.meta.overallMaturityScore.toFixed(2)}
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 'bold', color: '#138a8a', letterSpacing: '1px' }}>
                Institutional Maturity Score
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, color: 'white' }}>
                Average of IFIP station maturity ratings (updates when you change a station rating).
              </div>
            </div>
          </div>
        )}
        
        <h4>Portrait of {school?.officialName || 'School'}</h4>
        {isPreview ? (
          <div className="preview-text">{report.institutionalBrief?.executiveSummary || "No portrait provided."}</div>
        ) : (
          <textarea
            className="editor-textarea"
            style={{ minHeight: '200px' }}
            value={getEditorFieldValue(
              'institutionalBrief.executiveSummary',
              report.institutionalBrief?.executiveSummary
            )}
            onChange={(e) => queueEditorField('institutionalBrief.executiveSummary', e.target.value)}
            onBlur={(e) => commitEditorField('institutionalBrief.executiveSummary', e.target.value)}
            placeholder="AI will generate a public pen portrait of the school here..."
          />
        )}

        <h4 style={{ marginTop: '30px' }}>Summary of Maturity Ratings</h4>
        <div className="foundations-scorecard">
          <table>
            <thead>
              <tr>
                <th>Station</th>
                <th style={{ textAlign: 'center', width: '60px' }}>SEF</th>
                <th style={{ textAlign: 'center', width: '40px' }}></th>
                <th style={{ color: '#d11d7e' }}>IFIP Maturity Rating</th>
              </tr>
            </thead>
            <tbody>
            {[1,2,3,4,5,6,7].map(num => {
                const sId = `0${num}`;
                const selfLvl = audit.stations?.[sId]?.selectedLevel; 
                const ifipLvl = report.stationAnalysis?.[sId]?.ifipRating; 
                
                return (
                <tr key={num}>
                    <td><strong>{en.stations[num-1].title}</strong></td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#94a3b8' }}>{selfLvl || '-'}</td>
                    <td style={{ textAlign: 'center' }}>{getComparisonIcon(selfLvl, ifipLvl)}</td>
                    <td style={{ fontWeight: 'bold', color: '#d11d7e' }}>
                        {ifipLvl ? `${getLevelLabel(sId, ifipLvl)}` : 'Awaiting assessment...'}
                    </td>
                </tr>
                );
            })}
            </tbody>
          </table>
        </div>
        {!isPreview && (
          <div className="section-footer-approval">
            <button 
              onClick={() => toggleSectionApproval('brief')}
              className={`status-pill ${report.approvals?.brief ? 'reviewed' : ''}`}
            >
              {report.approvals?.brief ? '✓ SECTION 01 APPROVED' : 'MARK SECTION 01 AS REVIEWED'}
            </button>
          </div>
        )}
      </section>

      {/* 02. MATURITY MATRIX */}
      <section className="editor-card">
        <SectionHeader title="02. Maturity Matrix & Benchmarking" sectionKey="matrix" />
        
        {/* Stealth Benchmarking Logic: Auditor vs School View */}
        {!isPreview ? (
          <div className="benchmarking-workspace" style={{ background: 'rgba(0,0,0,0.2)', padding: '25px', borderRadius: '8px', border: '1px solid rgba(19, 138, 138, 0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
               <h5 style={{ margin: 0, color: '#138a8a', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Internal Benchmarking Analysis (Auditor Only)</h5>
               <div style={{ background: '#138a8a33', color: '#138a8a', fontSize: '0.6rem', padding: '4px 10px', borderRadius: '4px', fontWeight: 'bold' }}>DATASET: STEALTH MODE</div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '40px', alignItems: 'center' }}>
              <div>
                <label style={{ fontSize: '0.65rem', opacity: 0.5, display: 'block', marginBottom: '15px', textTransform: 'uppercase' }}>Performance vs. Global Mean (Current Cycle)</label>
                <div style={{ height: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', position: 'relative', marginTop: '25px' }}>
                  {/* Global Marker (Static Placeholder for 3.0) */}
                  <div style={{ position: 'absolute', left: '60%', top: '-20px', textAlign: 'center', transform: 'translateX(-50%)' }}>
                    <div style={{ width: '2px', height: '24px', background: '#fbbf24', margin: '0 auto' }}></div>
                    <span style={{ fontSize: '0.55rem', color: '#fbbf24', fontWeight: 'bold' }}>GLOBAL AVG (3.0)</span>
                  </div>
                  {/* School Marker */}
                  <div style={{ 
                    position: 'absolute', 
                    left: `${((report.meta?.overallMaturityScore || 0) / 5) * 100}%`, 
                    top: '-6px', 
                    width: '20px', 
                    height: '20px', 
                    background: '#d11d7e', 
                    borderRadius: '50%', 
                    transform: 'translateX(-50%)', 
                    border: '3px solid #1a2a44',
                    boxShadow: '0 0 15px rgba(209, 29, 126, 0.4)',
                    zIndex: 2
                  }}></div>
                </div>
              </div>
              
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.6', paddingLeft: '20px', borderLeft: '1px solid rgba(255,255,255,0.05)' }}>
                This institution is currently performing <strong style={{ color: '#fff' }}>{((report.meta?.overallMaturityScore || 0) - 3.0).toFixed(2)}</strong> points 
                relative to the global average for <strong style={{ color: '#138a8a' }}>{accreditationLevelLabel(school.accreditationLevel, { long: true })}</strong> institutions. 
                <br/><em style={{ fontSize: '0.7rem', display: 'block', marginTop: '10px', opacity: 0.6 }}>Note: This section is hidden from the school portal until the dataset reaches statistical significance.</em>
              </div>
            </div>
          </div>
        ) : (
          <div className="preview-placeholder-box" style={{ padding: '40px', textAlign: 'center', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '12px', background: 'rgba(255,255,255,0.01)' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '15px' }}>📊</div>
            <h4 style={{ margin: '0 0 10px 0', color: '#fff', fontSize: '1rem' }}>Global Benchmarking Aggregation</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#94a3b8', maxWidth: '500px', margin: '0 auto', lineHeight: '1.5' }}>
              IFIP is currently aggregating data for the {new Date().getFullYear()} accreditation cycle. 
              Comparative regional analysis and percentile rankings will be released to your portal once the global data set reaches statistical significance.
            </p>
          </div>
        )}
        {!isPreview && (
          <div className="section-footer-approval">
            <button 
              onClick={() => toggleSectionApproval('matrix')}
              className={`status-pill ${report.approvals?.matrix ? 'reviewed' : ''}`}
            >
              {report.approvals?.matrix ? '✓ SECTION 02 APPROVED' : 'MARK SECTION 02 AS REVIEWED'}
            </button>
          </div>
        )}
      </section>

      {/* 03. STATION ANALYSIS: SPLIT-PANE WORKSPACE */}
      <section className="editor-section">
        <SectionHeader title="03. Station-Based Analysis" sectionKey="stations" />
           
        {[1,2,3,4,5,6,7].map(num => {
          const sId = `0${num}`;
          const stationMeta = en.stations[num-1];
          const schoolData = audit.stations?.[sId] || { selectedLevel: 1, narrative: "" };
          const ifipData = report.stationAnalysis?.[sId] || { ifipRating: "", content: "", verification: { anchors: [], gaps: [] } };
          const stationEvidence = (audit.evidenceVault || []).filter(f => f.tags?.includes(sId));

          return (
            <div key={num} className="split-pane-station-row">
              <div className="pane-reference">
                <div className="pane-header">SCHOOL SUBMISSION: {stationMeta.title}</div>
                <div className="reference-content">
                  <div className="ref-rating-badge">SELF-RATING: <strong>Level {schoolData.selectedLevel}</strong></div>
                  <div className="ref-narrative-box">{schoolData.narrative || "No narrative submitted."}</div>
                  <div className="ref-evidence-links">
                    <label>LINKED EVIDENCE ({stationEvidence.length})</label>
                    <div className="evidence-chip-list">
                      {stationEvidence.map((file, idx) => (
                        <div key={idx} className="evidence-mini-link">
                          📄 {file.name}
                          <div style={{ fontSize: '0.65rem', opacity: 0.6, marginTop: '2px', fontStyle: 'italic' }}>
                            {distilledData?.factSheet?.find(f => f.fileName === file.name)?.evidenceSummary || "Reading file..."}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="pane-editor">
                <div className="pane-header" style={{ color: '#d11d7e' }}>IFIP AUDIT REPORTING</div>
                
                <div className="editor-controls">
                  <div className="rating-selector-group">
                    <label>IFIP MATURITY RATING</label>
                    <div className="inline-rating-wrapper">
                      <span className="inline-comparison-icon">{getComparisonIcon(schoolData.selectedLevel, ifipData.ifipRating)}</span>
                      <select 
                        className="maturity-select-inline"
                        value={ifipData.ifipRating || ""}
                        onChange={(e) => handleUpdate(`stationAnalysis.${sId}.ifipRating`, e.target.value)}
                        disabled={isPreview}
                      >
                        <option value="">Select Level...</option>
                        {[1,2,3,4,5].map(l => <option key={l} value={l}>{getLevelLabel(sId, l)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {isPreview ? (
                  <div className="preview-text">{ifipData.content || "Analysis pending..."}</div>
                ) : (
                  <>
                    <textarea 
                      className="editor-textarea"
                      style={{ minHeight: '180px' }}
                      value={localContent[sId] !== undefined ? localContent[sId] : (ifipData.content || "")}
                      onChange={(e) => setLocalContent({ ...localContent, [sId]: e.target.value })}
                      onBlur={(e) => handleUpdate(`stationAnalysis.${sId}.content`, e.target.value)}
                      placeholder={`Draft IFIP analysis for ${stationMeta.title}...`}
                    />
                    
                    <div className="ai-verification-insight">
                       <div className="insight-anchors">
                         <label>AI VERIFICATION ANCHORS</label>
                         <ul>
                           {(ifipData.verification?.anchors || []).map((a, i) => {
                             const text = formatVerificationItem(a);
                             return text ? <li key={i}>✓ {text}</li> : null;
                           })}
                         </ul>
                       </div>
                       <div className="insight-gaps">
                         <label>AI IDENTIFIED GAPS</label>
                         <ul>
                           {(ifipData.verification?.gaps || []).map((g, i) => {
                             const text = formatVerificationItem(g);
                             return text ? <li key={i}>⚠ {text}</li> : null;
                           })}
                         </ul>
                       </div>
                    </div>

                    <div className="station-footer-approval">
                      <button 
                        onClick={() => toggleSectionApproval(`station_${sId}`)}
                        className={`status-pill-mini ${report.approvals?.[`station_${sId}`] ? 'reviewed' : ''}`}
                      >
                        {report.approvals?.[`station_${sId}`] ? `✓ STATION ${sId} REVIEWED` : `MARK STATION ${sId} AS REVIEWED`}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
        
      </section>

      {/* 04. EVIDENCE PORTFOLIO & GAPS */}
      <section className="editor-card">
        <SectionHeader title="04. Evidence Portfolio & Gaps" sectionKey="evidence" />
        <h4>EVIDENCE VAULT REGISTRY</h4>
        <div className="evidence-registry-grid" style={{ marginBottom: '30px' }}>
          {(audit.evidenceVault || []).map((file, idx) => {
            const summary = distilledData?.factSheet?.find(f => f.fileName === file.name);
            return (
              <div
                key={idx}
                style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '10px', cursor: file.url ? 'pointer' : 'default' }}
                onClick={() => {
                  if (!file.url) return;
                  openEvidenceFileWithAccessLog({
                    schoolId,
                    file,
                    actor: user,
                    surface: 'report_editor',
                  });
                }}
                title={file.url ? 'Open file (access logged)' : undefined}
              >                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>📄 {file.name}</span>
                  <span style={{ fontSize: '0.6rem', color: '#138a8a', textTransform: 'uppercase' }}>{summary?.category || 'General'}</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  {summary?.evidenceSummary || "Processing file contents..."}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#138a8a', marginTop: '6px' }}>
                  <strong>VERIFICATION:</strong> {summary?.proves || "Identifying probative value..."}
                </div>
              </div>
            );
          })}
        </div>

        <h4>GAPS & OMISSIONS</h4>
        {isPreview ? (
            <div className="preview-text">{report.evidencePortfolio?.gaps}</div>
        ) : (
            <textarea 
              className="editor-textarea"
              style={{ minHeight: '100px' }}
              value={getEditorFieldValue('evidencePortfolio.gaps', report.evidencePortfolio?.gaps)}
              onChange={(e) => queueEditorField('evidencePortfolio.gaps', e.target.value)}
              onBlur={(e) => commitEditorField('evidencePortfolio.gaps', e.target.value)}
              placeholder="Targeted list of missing documents or evidence gaps required to reach next maturity level..."
            />
        )}
        {!isPreview && (
          <div className="section-footer-approval">
            <button 
              onClick={() => toggleSectionApproval('evidence')}
              className={`status-pill ${report.approvals?.evidence ? 'reviewed' : ''}`}
            >
              {report.approvals?.evidence ? '✓ SECTION 04 APPROVED' : 'MARK SECTION 04 AS REVIEWED'}
            </button>
          </div>
        )}
      </section>

      {/* 05. STRATEGIC ROADMAP */}
      <section className="editor-card">
        <SectionHeader title="05. Strategic Roadmap" sectionKey="roadmap" />
        <h4>THE "NEXT LEVEL" PLAN</h4>
        {isPreview ? (
            <div className="preview-text" style={{ marginBottom: '20px' }}>{report.strategicRoadmap?.content}</div>
        ) : (
            <textarea 
              className="editor-textarea"
              style={{ minHeight: '150px', marginBottom: '20px' }}
              value={getEditorFieldValue('strategicRoadmap.content', report.strategicRoadmap?.content)}
              onChange={(e) => queueEditorField('strategicRoadmap.content', e.target.value)}
              onBlur={(e) => commitEditorField('strategicRoadmap.content', e.target.value)}
              placeholder="Opening conclusion paragraph(s), then numbered lines (1. ... 2. ... each on its own line) for specific recommendations."
            />
        )}

        <div className="roadmap-side-by-side">
          <div className="roadmap-side-field">
            <h4>NEXT REVIEW TIMELINE</h4>
            {isPreview ? (
                <div className="roadmap-side-preview">{report.strategicRoadmap?.nextPhase}</div>
            ) : (
                <textarea
                  className="editor-textarea roadmap-side-input"
                  value={getEditorFieldValue('strategicRoadmap.nextPhase', report.strategicRoadmap?.nextPhase)}
                  onChange={(e) => queueEditorField('strategicRoadmap.nextPhase', e.target.value)}
                  onBlur={(e) => commitEditorField('strategicRoadmap.nextPhase', e.target.value)}
                  placeholder="Readiness for next accreditation tier (or continued work toward it with IFIP support), with indicative timing."
                />
            )}
          </div>
          <div className="roadmap-side-field">
            <h4>IFIP RESOURCE MATCHING</h4>
            {isPreview ? (
                <div className="roadmap-side-preview">{report.strategicRoadmap?.resources}</div>
            ) : (
                <textarea
                  className="editor-textarea roadmap-side-input"
                  value={getEditorFieldValue('strategicRoadmap.resources', report.strategicRoadmap?.resources)}
                  onChange={(e) => queueEditorField('strategicRoadmap.resources', e.target.value)}
                  onBlur={(e) => commitEditorField('strategicRoadmap.resources', e.target.value)}
                  placeholder="Tailored links to IFIP modules or partnerships..."
                />
            )}
          </div>
        </div>
        {!isPreview && (
          <div className="section-footer-approval">
            <button 
              onClick={() => toggleSectionApproval('roadmap')}
              className={`status-pill ${report.approvals?.roadmap ? 'reviewed' : ''}`}
            >
              {report.approvals?.roadmap ? '✓ SECTION 05 APPROVED' : 'MARK SECTION 05 AS REVIEWED'}
            </button>
          </div>
        )}
      </section>

      {/* 06. SIGN-OFF */}
      <section className="editor-card" style={{ background: isPreview ? 'white' : '#1e293b', border: isPreview ? '1px solid #e2e8f0' : 'none' }}>
        <SectionHeader title="06. Certification & Sign-Off" sectionKey="signoff" />
        <div className="cert-grid" style={{ display: 'grid', gridTemplateColumns: isPreview ? '1fr' : '1fr 1fr 1fr', gap: '40px' }}>
          
          {/* COLUMN 1: LEAD ACCREDITOR IDENTITY (Always Visible) */}
          <div className="cert-col">
            <h4 style={{ color: isPreview ? '#1a2a44' : '#138a8a', margin: '0 0 15px 0', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '900' }}>Lead Accreditor</h4>
            <div style={{ padding: '25px', background: isPreview ? '#f8fafc' : 'rgba(0,0,0,0.15)', borderRadius: '8px', border: isPreview ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: isPreview ? '#1a2a44' : 'white', marginBottom: '6px' }}>
                {leadAccreditorName}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#138a8a', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {leadAccreditorQuals}
              </div>
              <div style={{ fontSize: '0.8rem', color: isPreview ? '#64748b' : '#94a3b8', fontStyle: 'italic', lineHeight: '1.6' }}>
                {leadAccreditorBio}
              </div>
            </div>
          </div>

          {/* BADGE TOOLKIT: Visible to School to manage their digital identity */}
          {isPreview && (
            <div className="badge-toolkit" style={{ marginTop: '20px', padding: '25px', background: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1e293b', fontSize: '0.9rem', fontWeight: 'bold' }}>Digital Badge & Embed Code</h4>
              
              {isLocked ? (
                <>
                  <p style={{ fontSize: '0.75rem', color: '#d11d7e', fontWeight: 'bold', marginBottom: '15px', textTransform: 'uppercase' }}>
                    ⚠ Accreditation Expires: {displayExpiryDate}
                  </p>
                  
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                    {['medallion', 'banner', 'seal'].map(v => (
                      <button 
                        key={v} 
                        onClick={() => setBadgeVariant(v)} 
                        style={{ padding: '6px 12px', fontSize: '0.65rem', borderRadius: '4px', border: '1px solid #cbd5e1', background: badgeVariant === v ? '#138a8a' : 'white', color: badgeVariant === v ? 'white' : '#64748b', cursor: 'pointer', fontWeight: 'bold', textTransform: 'uppercase' }}
                      >
                        {v}
                      </button>
                    ))}
                  </div>

                  <div style={{ position: 'relative' }}>
                    <textarea 
                      readOnly
                      style={{ width: '100%', height: '100px', background: '#1e293b', color: '#94a3b8', padding: '15px', borderRadius: '4px', border: 'none', fontSize: '0.75rem', fontFamily: 'monospace', lineHeight: '1.4', resize: 'none' }}
                      value={`<!-- IFIP Accreditation Badge: ${badgeVariant.toUpperCase()} -->\n<div id="ifip-badge" data-level="${school.accreditationLevel}" data-variant="${badgeVariant}"></div>\n<script src="https://hub.ifip.group/embed/badge.js"></script>`}
                    />
                    <button 
                      onClick={(e) => {
                        const code = e.currentTarget.previousSibling.value;
                        navigator.clipboard.writeText(code);
                        showNotice('Badge code copied to clipboard.', 'success', 3500);
                      }}
                      style={{ position: 'absolute', bottom: '10px', right: '10px', background: '#138a8a', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '4px', fontSize: '0.65rem', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      COPY CODE
                    </button>
                  </div>
                  <small style={{ display: 'block', marginTop: '10px', fontSize: '0.65rem', color: '#64748b', fontStyle: 'italic' }}>
                    Paste this code into your website's HTML to display your live accreditation status.
                  </small>
                </>
              ) : (
                <div style={{ padding: '20px', background: '#e2e8f0', borderRadius: '4px', textAlign: 'center', color: '#64748b', fontSize: '0.75rem', border: '1px dashed #cbd5e1', fontFamily: 'monospace' }}>
                   Your live badge embed code will appear here once the report is finalised.
                </div>
              )}
            </div>
          )}

          {!isPreview && (
            <>
              {/* COLUMN 2: INTERNAL COMPLETION CHECKLIST */}
              <div className="cert-col" style={{ padding: '0 10px', borderLeft: '1px solid rgba(255,255,255,0.05)', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                <h4 style={{ color: '#138a8a', margin: '0 0 15px 0', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '900' }}>Auditor's Completion Status</h4>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', background: 'rgba(0,0,0,0.15)', padding: '20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  {requiredKeys.map(key => {
                    const isDone = report.approvals?.[key];
                    const label = key.startsWith('station_') ? key.split('_')[1].slice(1) : key.charAt(0).toUpperCase();
                    return (
                      <div key={key} style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => toggleSectionApproval(key)}
                          style={{
                            width: '32px', height: '32px', borderRadius: '4px', 
                            border: `1px solid ${isDone ? '#138a8a' : '#444'}`,
                            background: isDone ? '#138a8a' : 'transparent', 
                            color: isDone ? 'white' : '#666',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '4px', cursor: 'pointer'
                          }}
                        >
                          {label}
                        </button>
                        <span style={{ fontSize: '0.55rem', opacity: 0.4, textTransform: 'uppercase', display: 'block' }}>
                          {key.includes('_') ? key.split('_')[1] : key}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '15px', fontSize: '0.7rem', opacity: 0.6, lineHeight: '1.4' }}>
                  <strong>Status: {approvedCount} / {requiredKeys.length} Sections Finalised.</strong>
                  <br />Click the status tiles above to toggle section approval.
                </div>
              </div>

              {/* COLUMN 3: PEER REVIEWER ACTIONS */}
              <div className="cert-col" style={{ paddingLeft: '10px' }}>
                <h4 style={{ color: '#d11d7e', margin: '0 0 15px 0', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '900' }}>
                  {postAppealQa && school.workflowStep === 'peer_review' ? 'Post-Appeal QA' : 'Internal Peer Review'}
                </h4>
                <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: 'white', marginBottom: '2px' }}>{report.certification?.peerReviewerName || "Review Pending"}</div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '20px' }}>IFIP Quality Assurance Team</div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(() => {
                    const isSelfReview = (school?.assignedAuditorUid || report.certification?.accreditorUid) === user.uid;
                    const canOverride = permissions.canManageStaff;
                    const isSignoffDisabled = !isReadyToPublish || (!canOverride && isSelfReview) || !permissions.canPeerReview;
                    
                    if (showRejectionForm) return (
                      <div style={{ background: 'rgba(209, 29, 126, 0.05)', padding: '15px', borderRadius: '6px', border: '1px solid rgba(209, 29, 126, 0.2)' }}>
                        <textarea 
                          className="editor-textarea"
                          style={{ fontSize: '0.8rem', minHeight: '100px', marginBottom: '10px' }}
                          placeholder="Identify specific factual errors or requested revisions for the author..."
                          value={rejectionFeedback}
                          onChange={(e) => setRejectionFeedback(e.target.value)}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={handleRejectReport} className="btn-danger-text" style={{ fontSize: '0.75rem', padding: '8px 15px' }}>Send to Author</button>
                          <button onClick={() => setShowRejectionForm(false)} className="btn-secondary" style={{ fontSize: '0.75rem' }}>Cancel</button>
                        </div>
                      </div>
                    );

                    return (
                      <>
                        <label 
                          className="cert-label" 
                          style={{ 
                            opacity: isSignoffDisabled ? 0.5 : 1, 
                            background: (isSelfReview && canOverride) ? 'rgba(251, 191, 36, 0.1)' : 'rgba(19, 138, 138, 0.1)', 
                            color: (isSelfReview && canOverride) ? '#fbbf24' : '#138a8a', 
                            borderColor: (isSelfReview && canOverride) ? '#fbbf2433' : '#138a8a33',
                            margin: 0, padding: '15px', borderRadius: '8px', cursor: isSignoffDisabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            border: '1px solid'
                          }}
                          title={isSelfReview && canOverride ? "SUPER ADMIN OVERRIDE: Self-Reviewing your own audit." : isSelfReview ? "Conflict of Interest: You cannot peer-review your own audit." : !isReadyToPublish ? "Checklist incomplete." : ""}
                        >
                          <input 
                            type="checkbox" 
                            disabled={isSignoffDisabled}
                            checked={report.certification?.peerReviewSigned || false} 
                            style={{ width: '20px', height: '20px' }}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              setIsSaving(true);
                              try {
                                const reportRef = doc(db, "reports", schoolId);
                                await updateDoc(reportRef, {
                                  "certification.peerReviewSigned": checked,
                                  "certification.peerReviewerUid": user.uid,
                                  "certification.peerReviewerName": user.displayName,
                                  "meta.lastEditedAt": serverTimestamp()
                                });
                              } catch (err) { console.error(err); } finally { setIsSaving(false); }
                            }} 
                          />
                          <div>
                            <div style={{ fontWeight: 'bold' }}>
                              {isSelfReview && canOverride ? "Authorize Self-Review (Override)" : "Verify & Final Sign-Off"}
                            </div>
                            <div style={{ fontSize: '0.65rem', opacity: 0.7 }}>
                              {isSelfReview && canOverride ? "Internal controls overridden by Super Admin status." : "By checking this, you confirm the report meets IFIP quality standards."}
                            </div>
                          </div>
                        </label>

                        {report.certification?.peerReviewSigned && school.workflowStep === 'peer_review' && !postAppealQa && (
                          <button 
                            onClick={async () => {
                              if (school && schoolHasUnsettledExternalPayment(school)) {
                                const ok = confirmProceedDespiteUnsettledPayment(
                                  buildExternalSettlementWarning(school.officialName, 'release draft for fact-check')
                                );
                                if (!ok) return;
                              }
                              setIsSaving(true);
                              try {
                                await adminServices.releaseToSchool(schoolId, school.officialName, user.displayName);
                                showNotice('Report released to school for fact-check.', 'success');
                              } catch (err) {
                                showNotice('Failed to release: ' + err.message, 'error', 0);
                              } finally {
                                setIsSaving(false);
                              }
                            }}
                            className="btn-activate"
                            style={{ width: '100%', marginTop: '15px', padding: '12px', background: '#138a8a' }}
                            disabled={!permissions.canPublish}
                          >
                            Release Draft to School (Fact-Check)
                          </button>
                        )}

                        {report.certification?.peerReviewSigned && school.workflowStep === 'peer_review' && postAppealQa && (
                          <>
                            <button
                              type="button"
                              onClick={async () => {
                                if (school && schoolHasUnsettledExternalPayment(school)) {
                                  const ok = confirmProceedDespiteUnsettledPayment(
                                    buildExternalSettlementWarning(school.officialName, 'release revised draft for fact-check')
                                  );
                                  if (!ok) return;
                                }
                                setIsSaving(true);
                                try {
                                  await adminServices.releaseToSchool(schoolId, school.officialName, user.displayName);
                                  showNotice('Revised draft released to school for fact-check.', 'success');
                                } catch (err) {
                                  showNotice('Failed to release: ' + err.message, 'error', 0);
                                } finally {
                                  setIsSaving(false);
                                }
                              }}
                              className="btn-activate"
                              style={{ width: '100%', marginTop: '15px', padding: '12px', background: '#138a8a' }}
                              disabled={!(permissions.canPeerReview || permissions.canPublish)}
                            >
                              Send to School for Fact-Check
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm('Approve for finalisation without another school fact-check?')) return;
                                setIsSaving(true);
                                try {
                                  await adminServices.approveAppealForFinalisation(schoolId, school.officialName, user.displayName);
                                  showNotice('Approved for finalisation.', 'success');
                                } catch (err) {
                                  showNotice('Failed to approve: ' + err.message, 'error', 0);
                                } finally {
                                  setIsSaving(false);
                                }
                              }}
                              className="btn-secondary"
                              style={{
                                width: '100%',
                                marginTop: '10px',
                                padding: '12px',
                                borderColor: '#22c55e',
                                color: '#4ade80',
                              }}
                              disabled={!(permissions.canPeerReview || permissions.canPublish)}
                            >
                              Approve for Finalisation
                            </button>
                          </>
                        )}

                        {permissions.canPeerReview && isReadyToPublish && !isSelfReview && !report.certification?.peerReviewSigned && (
                          <button 
                            onClick={() => setShowRejectionForm(true)}
                            style={{ background: 'transparent', border: '1px solid #d11d7e', color: '#d11d7e', padding: '10px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold' }}
                          >
                            Request Changes (Reject Draft)
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReportEditor;